/**
 * Card image component with loading states
 */
import type { ImageUris } from '@/types/card';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useState } from 'react';

import { getSourceUrl } from '@/lib/imageUrl';

/** Accepts full ImageUris or partial with just small/normal */
export interface CardImageProps {
  imageUris: Partial<ImageUris> | null;
  name: string;
  /** Controls CSS display dimensions (default layout size); `fluid` fills its container's width */
  size?: 'small' | 'normal' | 'large' | 'fluid';
  /** Controls which source image to fetch — defaults to `size` (`normal` for `fluid`). Use to avoid upscaling. */
  sourceSize?: 'small' | 'normal' | 'large';
  className?: string;
  showPlaceholder?: boolean;
}

const dimensionStyles = {
  small: 'w-[73px] h-[102px]', // Half of small dimensions
  normal: 'w-[244px] h-[340px]', // Half of normal dimensions
  large: 'w-[336px] h-[468px]', // Half of large dimensions
  fluid: 'w-full aspect-[488/680]',
};

export default function CardImage({
  imageUris,
  name,
  size = 'normal',
  sourceSize,
  className,
  showPlaceholder = true,
}: CardImageProps): ReactElement {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const effectiveSource = sourceSize ?? (size === 'fluid' ? 'normal' : size);
  const imageUrl = getSourceUrl(imageUris, effectiveSource);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div className={clsx('bg-slate relative overflow-hidden rounded-lg', dimensionStyles[size], className)}>
      {/* Loading shimmer */}
      {isLoading && !hasError && <div className="from-slate via-storm to-slate absolute inset-0 animate-pulse bg-linear-to-r" />}

      {/* Error/Placeholder state */}
      {(hasError || !imageUrl) && showPlaceholder && (
        <div className="absolute inset-0 flex items-center justify-center p-2">
          <div className="text-center">
            <div className="bg-storm mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full">
              <svg className="text-ash h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <p className="text-ash truncate text-xs">{name}</p>
          </div>
        </div>
      )}

      {/* Actual image */}
      {imageUrl && !hasError && (
        <img
          src={imageUrl}
          alt={name}
          loading="lazy"
          onLoad={handleLoad}
          onError={handleError}
          className={clsx('h-full w-full object-cover transition-opacity duration-200', isLoading ? 'opacity-0' : 'opacity-100')}
        />
      )}
    </div>
  );
}
